import type { ChartData, ChartOptions } from "chart.js";
import { Bar } from "react-chartjs-2";
import type { StatsChart } from "@/stores/ContractorDetailStore";

const MONTHS_SHORT = [
  "Янв",
  "Фев",
  "Мар",
  "Апр",
  "Май",
  "Июн",
  "Июл",
  "Авг",
  "Сен",
  "Окт",
  "Ноя",
  "Дек",
];

export function BarChart({
  data,
  color = "#6366f1",
  inactiveColor,
  currentMonth,
  formatValue,
  height = 120,
  barThickness,
  testId = "bar-chart",
}: {
  data: StatsChart[];
  color?: string;
  inactiveColor?: string;
  currentMonth?: number;
  formatValue?: (v: number) => string;
  height?: number;
  barThickness?: number;
  testId?: string;
}) {
  if (data.length === 0) return null;

  // показывать только прошедшие + текущий месяц (будущие — нули, скрываем)
  const maxIdx =
    currentMonth != null ? Math.min(currentMonth, data.length) : data.length;
  const visibleData = data.slice(0, maxIdx);

  const vals = visibleData.map((d) => d.value ?? d.cumulative ?? 0);
  const labels = visibleData.map(
    (d, i) => MONTHS_SHORT[(d.month ?? i + 1) - 1] ?? MONTHS_SHORT[i % 12],
  );
  const inactive = inactiveColor ?? `${color}55`;

  const chartData: ChartData<"bar"> = {
    labels,
    datasets: [
      {
        data: vals,
        backgroundColor: visibleData.map((d) =>
          d.month === currentMonth ? color : inactive,
        ),
        hoverBackgroundColor: visibleData.map(() => color),
        borderRadius: 4,
        borderSkipped: false,
        ...(barThickness != null ? { barThickness } : {}),
      },
    ],
  };

  // Плагин: сплошная полоска под всеми барами
  const barTrackPlugin = {
    id: "barTrack",
    beforeDatasetsDraw(chart: import("chart.js").Chart) {
      const { ctx, scales } = chart;
      const meta = chart.getDatasetMeta(0);
      if (!meta?.data?.length) return;
      const first = meta.data[0] as import("chart.js").BarElement & {
        x: number;
        width: number;
      };
      const last = meta.data[
        meta.data.length - 1
      ] as import("chart.js").BarElement & { x: number; width: number };
      if (!first || !last) return;
      const x = first.x - first.width / 2;
      const xEnd = last.x + last.width / 2;
      const y = scales.y.getPixelForValue(0);
      ctx.save();
      ctx.fillStyle = "#e5e7eb";
      ctx.beginPath();
      ctx.roundRect(x, y - 0.5, xEnd - x, 0.5, 0.25);
      ctx.fill();
      ctx.restore();
    },
  };

  const options: ChartOptions<"bar"> = {
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: "#ffffff",
        borderColor: "#e5e7eb",
        borderWidth: 1,
        titleColor: "#9ca3af",
        bodyColor: "#111827",
        titleFont: { size: 11, family: "var(--font-onest, sans-serif)" },
        bodyFont: {
          size: 13,
          family: "var(--font-onest, sans-serif)",
          weight: "bold",
        },
        padding: 10,
        cornerRadius: 8,
        displayColors: false,
        caretSize: 5,
        callbacks: {
          title: (contexts) => {
            const i = contexts[0].dataIndex;
            return MONTHS_SHORT[(visibleData[i]?.month ?? i + 1) - 1] ?? "";
          },
          label: (context) => {
            const y = context.parsed.y;
            if (y == null) return "";
            return formatValue ? formatValue(y) : y.toLocaleString("ru-RU");
          },
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: {
          font: { size: 9, family: "var(--font-onest, sans-serif)" },
          color: (ctx) =>
            visibleData[ctx.index]?.month === currentMonth
              ? "#6b7280"
              : "#c4c4c4",
          maxRotation: 0,
        },
        border: { display: false },
      },
      y: { display: false },
    },
    interaction: { mode: "index", intersect: false },
  };

  return (
    <div
      style={{ width: "100%", height }}
      className="scd-chart-area"
      data-testid={testId}
    >
      <Bar data={chartData} options={options} plugins={[barTrackPlugin]} />
    </div>
  );
}
